import { KARAOKE_TRACK_NUMBER } from '../Timeline';
import { Track } from '../Track';
import { KaraokeClip } from './KaraokeClip';

import type { TranscriptToken } from '../../types';
import type { KaraokeData } from '../../types/karaoke';
import type { TextElement } from '../elements/TextElement';

export class KaraokeTrack extends Track {
  private _karaokeClips: KaraokeClip[];

  constructor(props: Partial<KaraokeTrack & Track> = {}) {
    super(props);
    this._karaokeClips = props.karaokeClips ? [...props.karaokeClips] : [];
  }

  /**
   * Elements in the track as text elements
   */
  override get elements(): TextElement[] {
    return this._karaokeClips;
  }

  /**
   * Karaoke clips in the track
   */
  get karaokeClips(): KaraokeClip[] {
    return this._karaokeClips;
  }

  set karaokeClips(clips: KaraokeClip[]) {
    this._karaokeClips = clips;
  }

  /**
   * Creates a KaraokeTrack from karaoke data and transcript tokens
   * Optimized for large token arrays (~10,000 elements) using O(1) token lookup
   *
   * @param karaokeData - The karaoke data containing clips and configurations
   * @param transcript - Full array of transcript tokens (used for efficient token lookup)
   * @returns A new KaraokeTrack instance
   */
  static fromKaraokeDataAndTranscript(
    karaokeData: KaraokeData,
    transcript: TranscriptToken[],
  ): KaraokeTrack {
    const { clips, configs, defaultConfigId } = karaokeData;

    // Get default config or create a fallback
    const defaultConfig = defaultConfigId
      ? configs.get(defaultConfigId)
      : configs.values().next().value;

    if (!defaultConfig) {
      throw new Error('No karaoke configuration available');
    }

    // OPTIMIZATION: Create a token lookup map for O(1) access
    // For ~10,000 tokens, this is MUCH faster than repeated array lookups
    // Map creation: O(n), Lookup per clip: O(1) instead of O(n)
    // Total complexity: O(n + m) instead of O(n * m) where:
    //   n = number of tokens (~10,000)
    //   m = number of clips (~100-500)
    const tokenLookup = new Map<string, TranscriptToken>();
    for (let i = 0; i < transcript.length; i++) {
      const token = transcript[i];
      tokenLookup.set(token.id, token);
    }

    // Convert KaraokeLine[] to KaraokeClip[]
    const karaokeClips = clips.map(clip => {
      // Get config for this line, or use default
      const config = clip.karaokeConfigId
        ? configs.get(clip.karaokeConfigId) || defaultConfig
        : defaultConfig;

      // If tokens are already in the line, use them directly
      // Otherwise, look them up from the full transcript using the efficient map
      let tokensForClip: TranscriptToken[] = [];

      if (clip.transcriptTokenIds && clip.transcriptTokenIds.length > 0) {
        tokensForClip = clip.transcriptTokenIds
          .map(id => tokenLookup.get(id))
          .filter((token): token is TranscriptToken => token !== undefined);
      }

      if (tokensForClip.length !== clip.transcriptTokenIds.length) {
        console.warn(
          `Some tokens for karaoke clip ${clip.id} were not found in the transcript.`,
        );
      }

      return new KaraokeClip({
        id: clip.id,
        transcriptTokens: tokensForClip,
        hiddenTranscriptTokenIds: clip.hiddenTranscriptTokenIds || [],
        ignoredByFiltersIds: clip.ignoredByFiltersIds || [],
        startSecondsOffset: clip.startSecondsOffset,
        endSecondsOffset: clip.endSecondsOffset,
        karaokeConfig: config,
        track: KARAOKE_TRACK_NUMBER,
      });
    });

    return new KaraokeTrack({
      id: String(KARAOKE_TRACK_NUMBER),
      type: 'text',
      karaokeClips,
    });
  }
}
